'use client';

import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDate } from 'date-fns';
import dayjs from 'dayjs';

import { DashCards, StatusSelect } from '~/entities';
import { DashList } from '~/entities/SponsoredProductsDashboard';
import { useGetDashboard } from '~/entities/SponsoredProductsDashboard/hooks/useGetDashboard';
import { useGetProductsSold } from '~/entities/SponsoredProductsDashboard/hooks/useGetProductsSold';
import { ContentTitle, StyledContainer, StyledTableContainer } from '~/shared';
import { CampaignStatus, DashCampaignsDto } from '~/types';

export function SponsoredDashboardView() {
  const [status, setStatus] = useState<'' | `${CampaignStatus}`>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [finalDate, setFinalDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState('');
  const [finalDateError, setFinalDateError] = useState('');

  const [getDashboardFilter, setGetDashboardFilter] =
    useState<DashCampaignsDto>({});

  const getDashboardQuery = useGetDashboard(getDashboardFilter);
  const getProductsSoldQuery = useGetProductsSold(getDashboardFilter);

  useEffect(() => {
    if (startDate === null) {
      setStartDateError('');
      return;
    }
    if (Number.isNaN(startDate.getTime())) {
      setStartDateError('Data invalida');
      return;
    }
    setStartDateError('');
  }, [startDate]);

  useEffect(() => {
    if (finalDate === null) {
      setFinalDateError('');
      return;
    }
    if (Number.isNaN(finalDate.getTime())) {
      setFinalDateError('Data invalida');
      return;
    }
    if (startDate && finalDate < startDate) {
      setFinalDateError('A data final não pode ser menor que a data inicial');
      return;
    }
    setFinalDateError('');
  }, [finalDate, startDate]);

  const DisableRetroactiveDates = (date: Date | null) => {
    if (!startDate || !date) return false;
    const dayjsDate = dayjs(date);
    const dayjsStartDate = dayjs(startDate);
    return dayjsDate.isBefore(dayjsStartDate.startOf('day'));
  };

  useDebounce(
    () => {
      setGetDashboardFilter(state => ({
        ...state,
        status: status === '' ? undefined : status,
      }));
    },
    1,
    [status]
  );

  useDebounce(
    () => {
      if (startDate === null) {
        setStartDateError('');
        setGetDashboardFilter(state => ({ ...state, startDate: undefined }));
        return;
      }

      if (Number.isNaN(startDate.getTime())) {
        setStartDateError('Data invalida');
        return;
      }

      setStartDateError('');
      setGetDashboardFilter(state => ({
        ...state,
        startDate: formatDate(startDate, 'dd-MM-yyyy'),
      }));
    },
    1,
    [startDate]
  );

  useDebounce(
    () => {
      if (finalDate === null) {
        setFinalDateError('');
        setGetDashboardFilter(state => ({ ...state, finishDate: undefined }));
        return;
      }

      if (Number.isNaN(finalDate.getTime())) {
        setFinalDateError('Data invalida');
        return;
      }

      if (startDate && finalDate < startDate) {
        setFinalDateError('A data final não pode ser menor que a data inicial');
        return;
      }

      setFinalDateError('');
      setGetDashboardFilter(state => ({
        ...state,
        finishDate: formatDate(finalDate, 'dd-MM-yyyy'),
      }));
    },
    1,
    [finalDate]
  );

  return (
    <>
      <ContentTitle
        title="Dashboard de Patrocinados"
        isFetching={getDashboardQuery.isLoading || getDashboardQuery.isFetching}
        actions={
          <>
            <DatePicker
              sx={{ width: '100%' }}
              label="Data inicial"
              value={startDate}
              onChange={value => setStartDate(value)}
              slotProps={{
                textField: {
                  error: !!startDateError,
                  helperText: startDateError,
                  variant: 'filled',
                },
                actionBar: {
                  actions: ['clear'],
                },
              }}
            />

            <DatePicker
              sx={{ width: '100%' }}
              label="Data final"
              value={finalDate}
              onChange={value => setFinalDate(value)}
              slotProps={{
                textField: {
                  error: !!finalDateError,
                  helperText: finalDateError,
                  variant: 'filled',
                },
                actionBar: {
                  actions: ['clear'],
                },
              }}
              shouldDisableDate={DisableRetroactiveDates}
            />

            <StatusSelect
              fullWidth
              selectedStatus={status}
              setter={setStatus}
            />
          </>
        }
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          width: '100%',
        }}
      >
        <DashCards
          getDashboardsQuery={getDashboardQuery}
          getProductsSoldQuery={getProductsSoldQuery}
        />

        <StyledContainer>
          <StyledTableContainer>
            <Box
              sx={() => ({
                display: 'flex',
                justifyContent: 'space-between',
              })}
            >
              <Typography sx={() => ({ typography: 'h6' })}>
                Melhores campanhas
              </Typography>
              <Link href="/patrocinados">
                <Typography
                  sx={() => ({
                    textDecoration: 'underline',
                    color: '#2B52DD',
                    cursor: 'pointer',
                    fontWeight: '500',
                  })}
                >
                  Ver todos os patrocinados
                </Typography>
              </Link>
            </Box>

            <DashList getDashboardQuery={getDashboardQuery} />
          </StyledTableContainer>
        </StyledContainer>
      </div>
    </>
  );
}
